import dayjs from "dayjs";

export type StatsPeriodPreset =
  | "next7"
  | "last7"
  | "last30"
  | "last90"
  | "thisWeek"
  | "thisMonth"
  | "lastMonth"
  | "thisYear"
  | "custom";

export type StatsRange = { fromDay: string; toDay: string };

export type StatsPeriod = { preset: StatsPeriodPreset; range: StatsRange };

export const MAX_STATS_DAYS = 366;

export const STATS_PERIOD_OPTIONS: { id: StatsPeriodPreset; name: string }[] = [
  { id: "last7", name: "Ultimi 7 giorni" },
  { id: "last30", name: "Ultimi 30 giorni" },
  { id: "last90", name: "Ultimi 90 giorni" },
  { id: "thisWeek", name: "Questa settimana" },
  { id: "thisMonth", name: "Questo mese" },
  { id: "lastMonth", name: "Mese scorso" },
  { id: "thisYear", name: "Quest'anno" },
  { id: "custom", name: "Personalizzato" },
];

export const ASSIGNMENT_PERIOD_OPTIONS: {
  id: StatsPeriodPreset;
  name: string;
}[] = [{ id: "next7", name: "Prossimi 7 giorni" }, ...STATS_PERIOD_OPTIONS];

const DAY_FORMAT = "YYYY-MM-DD";

type CurrentPeriodPreset = "thisWeek" | "thisMonth" | "thisYear";

function today(): dayjs.Dayjs {
  return dayjs().tz("Europe/Rome");
}

function currentPeriodBounds(preset: CurrentPeriodPreset): {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
} {
  const now = today();
  if (preset === "thisWeek") {
    const monday = now.subtract((now.day() + 6) % 7, "day");
    return { start: monday, end: monday.add(6, "day") };
  }
  const unit = preset === "thisMonth" ? "month" : "year";
  return { start: now.startOf(unit), end: now.endOf(unit) };
}

function isCurrentPeriod(
  preset: StatsPeriodPreset,
): preset is CurrentPeriodPreset {
  return (
    preset === "thisWeek" || preset === "thisMonth" || preset === "thisYear"
  );
}

export function presetRange(
  preset: Exclude<StatsPeriodPreset, "custom">,
): StatsRange {
  const now = today();
  if (preset === "next7") {
    return {
      fromDay: now.format(DAY_FORMAT),
      toDay: now.add(6, "day").format(DAY_FORMAT),
    };
  }
  if (isCurrentPeriod(preset)) {
    return {
      fromDay: currentPeriodBounds(preset).start.format(DAY_FORMAT),
      toDay: now.format(DAY_FORMAT),
    };
  }
  if (preset === "lastMonth") {
    const lastMonth = now.subtract(1, "month");
    return {
      fromDay: lastMonth.startOf("month").format(DAY_FORMAT),
      toDay: lastMonth.endOf("month").format(DAY_FORMAT),
    };
  }
  const days = { last7: 7, last30: 30, last90: 90 }[preset];
  return {
    fromDay: now.subtract(days - 1, "day").format(DAY_FORMAT),
    toDay: now.format(DAY_FORMAT),
  };
}

export function rangeIncludingFuture(period: StatsPeriod): StatsRange {
  if (!isCurrentPeriod(period.preset)) return period.range;
  const { start, end } = currentPeriodBounds(period.preset);
  return { fromDay: start.format(DAY_FORMAT), toDay: end.format(DAY_FORMAT) };
}

export function defaultStatsPeriod(): StatsPeriod {
  return { preset: "last30", range: presetRange("last30") };
}

export function rangeError(range: StatsRange): string | null {
  const days = dayjs(range.toDay).diff(dayjs(range.fromDay), "day") + 1;
  return days < 1 || days > MAX_STATS_DAYS
    ? `Il periodo deve essere tra 1 e ${MAX_STATS_DAYS} giorni.`
    : null;
}
